import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Box, Plus, Trash2 } from 'lucide-react'
import { SearchInput } from '../components/SearchInput'
import { getCups, createCup, resetAllBrainerData } from '../api/brainer'
import { Modal } from '../components/Modal'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { EmptyState } from '../components/EmptyState'
import { useDateFmt } from '../hooks/useDateFmt'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import type { BCup } from '../types'

export function BrainerCups() {
  const fmt = useDateFmt()
  const qc = useQueryClient()
  const navigate = useNavigate()

  const { data: cups = [], isLoading } = useQuery({ queryKey: ['b-cups'], queryFn: () => getCups().then((r) => r.data) })

  const [showReset, setShowReset] = useState(false)
  const reset = useMutation({
    mutationFn: resetAllBrainerData,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['b-cups'] })
      qc.invalidateQueries({ queryKey: ['b-traits'] })
      qc.invalidateQueries({ queryKey: ['b-coffees'] })
      setShowReset(false)
    },
  })

  const [showCreate, setShowCreate] = useState(false)
  const [cForm, setCForm] = useState({ title: '', description: '', disposable: false })
  const create = useMutation({
    mutationFn: () => createCup({ title: cForm.title, description: cForm.description || undefined, disposable: cForm.disposable }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-cups'] }); setShowCreate(false); setCForm({ title: '', description: '', disposable: false }) },
  })

  const [search, setSearch] = useState('')
  const filtered = cups.filter((c: BCup) =>
    [c.title, c.description ?? ''].some((v) => v.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Cups</h1>
          <p className="text-sm text-ink-dim mt-0.5">Data model schemas — each cup is a typed record template</p>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search cups" />
          <Button variant="danger" icon={Trash2} onClick={() => setShowReset(true)}>Reset data</Button>
          <Button icon={Plus} onClick={() => setShowCreate(true)}>New cup</Button>
        </div>
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
        ) : cups.length === 0 ? (
          <EmptyState icon={Box} title="No cups" description="Create a data schema to start storing records" />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Disposable</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c: BCup) => (
                <tr key={c.id} onClick={() => navigate(`/brainer/cups/${c.id}`)}
                  className="cursor-pointer">
                  <td>
                    <p className="font-medium text-ink">{c.title}</p>
                    {c.description && <p className="text-xs text-ink-dim mt-0.5">{c.description}</p>}
                  </td>
                  <td>
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c.disposable ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
                      {c.disposable ? 'Disposable' : 'Persistent'}
                    </span>
                  </td>
                  <td className="text-ink-dim">{fmt(c.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showReset && (
        <ConfirmDialog
          title="Reset all data"
          message="This will permanently delete all cups, traits, relations, coffees, beans, and flavors for this workspace. This cannot be undone."
          confirmLabel={reset.isPending ? 'Resetting…' : 'Reset everything'}
          loading={reset.isPending}
          onConfirm={() => reset.mutate()}
          onCancel={() => setShowReset(false)}
        />
      )}

      {showCreate && (
        <Modal title="New cup" onClose={() => setShowCreate(false)} size="sm">
          <CupForm form={cForm} onChange={setCForm} />
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={() => create.mutate()} disabled={!cForm.title || create.isPending} loading={create.isPending}>
              {create.isPending ? 'Creating' : 'Create'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}

function CupForm({ form, onChange }: { form: { title: string; description: string; disposable: boolean }; onChange: (f: any) => void }) {
  return (
    <div className="space-y-4">
      <Field label="Title *">
        <input value={form.title} onChange={(e) => onChange((f: any) => ({ ...f, title: e.target.value }))} placeholder="e.g. Customer"
          className="field" autoFocus />
      </Field>
      <Field label="Description">
        <input value={form.description} onChange={(e) => onChange((f: any) => ({ ...f, description: e.target.value }))} placeholder="Optional"
          className="field" />
      </Field>
      <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
        <input type="checkbox" checked={form.disposable} onChange={(e) => onChange((f: any) => ({ ...f, disposable: e.target.checked }))} className="rounded" />
        Disposable (records can be deleted after use)
      </label>
    </div>
  )
}
